/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import axios from "axios";
import InputField from "components/fields/InputField";
import {
  Button,
  Radio,
  RadioGroup,
  Stack,
  FormLabel,
  Select,
} from "@chakra-ui/react";
import { Tooltip, IconButton } from "@mui/material";
import { MdInfoOutline } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import AccountResetPostbackSecretConfirmModal from "../../shared/AccountResetPostbackSecretConfirmModal";
import AccountResetPostbackSecretSuccessModal from "../../shared/AccountResetPostbackSecretSuccessModal";

const AccountSettingsTab: React.FC<{
  detail: any;
  setSwalProps: any;
  setIsProcessing: any;
  isProcessing: boolean;
  fetchAccountDetail: any;
}> = ({
  detail,
  setSwalProps,
  setIsProcessing,
  isProcessing,
  fetchAccountDetail,
}) => {
  const [accountDetailForEdit, setAccountDetailForEdit] = useState<any>(null);
  const [isInvalidURL, setIsInvalidURL] = useState<boolean>(false);
  const [postbackSecret, setPostbackSecret] = useState<string>("");
  const [
    isOpenResetPostbackSecretConfirmModal,
    setIsOpenResetPostbackSecretConfirmModal,
  ] = useState<boolean>(false);
  const [resetPostbackSecretInfo, setResetPostbackSecretInfo] =
    useState<any>(null);
  const [
    isOpenResetPostbackSecretSuccessModal,
    setIsOpenResetPostbackSecretSuccessModal,
  ] = useState<boolean>(false);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  useEffect(() => {
    setAccountDetailForEdit(detail);
    if (detail?.postback_secret) {
      setPostbackSecret(detail.postback_secret);
    }
  }, [detail]);

  const showResetPostbackSecretConfirmModal = () => {
    setIsOpenResetPostbackSecretConfirmModal(true);
  };

  const closeResetPostbackSecretConfirmModal = () => {
    setIsOpenResetPostbackSecretConfirmModal(false);
  };

  const showResetPostbackSecretSuccessModal = () => {
    setIsOpenResetPostbackSecretSuccessModal(true);
  };

  const closeResetPostbackSecretSuccessModal = () => {
    setResetPostbackSecretInfo(null);
    setIsOpenResetPostbackSecretSuccessModal(false);
  };

  const isURL = (str: string): boolean => {
    const pattern =
      /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?(\?.*)?(#.*)?$/i;
    return pattern.test(str);
  };

  const handleInputChange = async (event: any) => {
    setIsInvalidURL(false);
    const { id, value, type, checked } = event.target;

    if (type === "checkbox") {
      setAccountDetailForEdit({
        ...accountDetailForEdit,
        [id]: checked ? "yes" : "no",
      });
    } else {
      setAccountDetailForEdit({ ...accountDetailForEdit, [id]: value });
      if (id === "postback_url") {
        if (!isURL(value)) {
          setIsInvalidURL(true);
        }
      }
    }
  };

  const isFormValid = accountDetailForEdit && accountDetailForEdit.postback_url;

  const handleSubmit = async () => {
    if (isFormValid) {
      const postData = {
        id: accountDetailForEdit.id,
        postback_url: accountDetailForEdit.postback_url,
        is_masked: accountDetailForEdit.setting_obj?.is_masked || "no",
        username: localStorage.getItem("username"),
        userRole: localStorage.getItem("userRole"),
      };

      try {
        setIsProcessing(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${localStorage
                .getItem("userRole")
                .toLowerCase()}/subaccount/action/account-update-settings`,
            postData
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: `Settings has been updated`,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsProcessing(false);
                },
              });
            } else {
              setIsProcessing(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsProcessing(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsProcessing(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsProcessing(false);
              },
            });
          });
      } catch (error) {
        setIsProcessing(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsProcessing(false);
          },
        });
      }
    }
  };

  return (
    <>
      {isOpenResetPostbackSecretConfirmModal && accountDetailForEdit && (
        <AccountResetPostbackSecretConfirmModal
          details={accountDetailForEdit}
          isOpen={isOpenResetPostbackSecretConfirmModal}
          onClose={closeResetPostbackSecretConfirmModal}
          setSwalProps={setSwalProps}
          showResetPostbackSecretSuccessModal={
            showResetPostbackSecretSuccessModal
          }
          setResetPostbackSecretInfo={setResetPostbackSecretInfo}
        />
      )}
      {isOpenResetPostbackSecretSuccessModal && resetPostbackSecretInfo && (
        <AccountResetPostbackSecretSuccessModal
          details={resetPostbackSecretInfo}
          isOpen={isOpenResetPostbackSecretSuccessModal}
          onClose={closeResetPostbackSecretSuccessModal}
          triggerReload={fetchAccountDetail}
        />
      )}

      {accountDetailForEdit && accountDetailForEdit !== null ? (
        <div className="flex px-4 py-8">
          <div className="w-full">
            <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                Postback URL
              </div>

              <div className="mb-5">
                <div className="w-full md:w-1/2">
                  <InputField
                    hideLabel
                    id="postback_url"
                    label="Postback URL"
                    placeholder="https://example.com"
                    extra=""
                    disabled={isProcessing}
                    variant="auth"
                    value={accountDetailForEdit.postback_url}
                    onChange={handleInputChange}
                    error={
                      isInvalidURL
                        ? "Invalid URL format. Please enter a valid URL, e.g. https://example.com"
                        : undefined
                    }
                  />
                </div>
              </div>

              <div className="mb-2 mt-6 text-sm font-semibold text-navy-700 dark:text-white">
                Data Privacy
              </div>
              <div>
                <label className="flex cursor-pointer items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_masked"
                    checked={
                      accountDetailForEdit.setting_obj?.is_masked === "yes"
                    }
                    onChange={(e) =>
                      setAccountDetailForEdit({
                        ...accountDetailForEdit,
                        setting_obj: {
                          ...accountDetailForEdit.setting_obj,
                          is_masked: e.target.checked ? "yes" : "no",
                        },
                      })
                    }
                    disabled={isProcessing}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-navy-700">
                    Mask Account Data
                  </span>
                </label>
              </div>

              <div className="pt-4">
                <Button
                  onClick={handleSubmit}
                  isDisabled={!isFormValid || isProcessing || isInvalidURL}
                  isLoading={isProcessing}
                  loadingText="Processing..."
                  className="btn btn-primary !px-10"
                >
                  Save
                </Button>
              </div>
            </div>

            {(userRole === "superadmin" || userRole === "merchant") && (
              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                  Webhook Secret
                </div>
                <div className="mb-6">
                  <div className="flex w-full md:w-1/2">
                    <input
                      type="text"
                      value={postbackSecret}
                      onKeyDown={(e) => e.preventDefault()}
                      className="caret-transparent h-10 w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 text-sm text-gray-700 shadow-sm outline-none"
                    />
                    <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                      <Tooltip
                        title="You will only see the unmask Webhook Secret once, immediately after it's generated."
                        arrow
                        placement="top"
                      >
                        <IconButton
                          id="button-info-postback-secret"
                          size="small"
                        >
                          <MdInfoOutline className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title="Reset Webhook Secret"
                        arrow
                        placement="top"
                      >
                        <IconButton
                          disabled={isProcessing}
                          id="button-reset-postback-secret"
                          size="small"
                          onClick={showResetPostbackSecretConfirmModal}
                        >
                          <TbRefresh className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="pb-5 pl-10 pr-10 pt-5">
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
        </div>
      )}
    </>
  );
};

export default AccountSettingsTab;
